import type { PlanEditTargetKind } from "@sts/geometry";
import { ApiError } from "../api/client.js";
import type { PlanVersionConflict } from "../api/types.js";

/**
 * What to say when a save to the floor plan was refused because someone else
 * got there first (`409 version_conflict`, migration 0046).
 *
 * The plan's corrections and objects are shared: the designer in the office,
 * a colleague in another tab, the surveyor. A change is sent with the version
 * of each thing it was made on; when that thing has moved on, the API writes
 * none of the change and answers with what it is now, who changed it and
 * when. The page then shows THEIR version — never quietly re-sends ours over
 * it — and says so in one plain sentence:
 *
 *   "Ada Owner changed this wall at 14:02 — your change wasn't saved. The
 *    plan now shows their version."
 */

/** The conflicts a refused save carried, or null when `error` is not a version conflict. */
export function versionConflicts<C extends PlanVersionConflict<unknown>>(error: unknown): readonly C[] | null {
  if (!(error instanceof ApiError) || error.status !== 409 || error.code !== "version_conflict") return null;
  const list = error.details?.["conflicts"];
  return Array.isArray(list) && list.length > 0 ? (list as C[]) : null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * " at 14:02" for today, " on 10 Sep at 14:02" for another day — on the
 * reader's own clock, since "14:02" is what they would have seen on theirs.
 * Empty when the time is not known.
 */
export function whenSaid(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const time = `${pad(at.getHours())}:${pad(at.getMinutes())}`;
  const today =
    at.getFullYear() === now.getFullYear() && at.getMonth() === now.getMonth() && at.getDate() === now.getDate();
  return today ? ` at ${time}` : ` on ${at.getDate()} ${MONTHS[at.getMonth()]} at ${time}`;
}

/** How one kind of thing on the plan is named in the sentence. */
export interface ConflictNaming {
  /** "wall", "radiator": "changed this wall". */
  readonly thing: string;
  /** What removing it is called: "reverted this wall to the scan", "took this radiator off the plan". */
  readonly removed: string;
  /** Said after a removal, if the plan shows something for it: "The plan now shows the wall as scanned." */
  readonly afterRemoval?: string;
}

export interface ConflictMessageOptions {
  /** The signed-in user: a change of their own from another tab is "You … in another tab or window". */
  readonly meId?: string;
  /** "removal": the refused save was taking the thing away, so "it wasn't removed". */
  readonly attempted?: "change" | "removal";
  readonly now?: Date;
}

/** The one sentence (two, with what the plan now shows) for a single conflict. */
export function conflictMessage(
  conflict: PlanVersionConflict<unknown>,
  naming: ConflictNaming,
  opts: ConflictMessageOptions = {},
): string {
  const mine = opts.meId !== undefined && conflict.by?.id === opts.meId;
  const subject = mine ? "You" : conflict.by?.name.trim() || "Someone else";
  const where = mine ? " in another tab or window" : "";
  const removed = conflict.change === "removed";
  const did = removed ? naming.removed : `changed this ${naming.thing}`;
  const outcome =
    opts.attempted === "removal" && !removed ? "it wasn't removed" : `${mine ? "this" : "your"} change wasn't saved`;
  const shows = removed
    ? naming.afterRemoval
      ? ` ${naming.afterRemoval}`
      : ""
    : ` The plan now shows ${mine ? "that" : "their"} version.`;
  return `${subject} ${did}${where}${whenSaid(conflict.at, opts.now)} — ${outcome}.${shows}`;
}

/** How a correction's element is named: a wall, a window/door/opening (by its kind when known), a room's name. */
export function editNaming(targetKind: PlanEditTargetKind, openingKind?: unknown): ConflictNaming {
  if (targetKind === "wall") {
    return {
      thing: "wall",
      removed: "reverted this wall to the scan",
      afterRemoval: "The plan now shows the wall as scanned.",
    };
  }
  if (targetKind === "room") {
    return {
      thing: "room's name",
      removed: "put this room's name back to the scan's",
      afterRemoval: "The plan now shows the scan's name for it.",
    };
  }
  const thing = openingKind === "window" || openingKind === "door" ? openingKind : "opening";
  return {
    thing,
    removed: `reverted this ${thing} to the scan`,
    afterRemoval: `The plan now shows the ${thing} as scanned.`,
  };
}

const NOUNS: Readonly<Record<PlanEditTargetKind | "mixed", readonly [string, string]>> = {
  wall: ["wall", "walls"],
  opening: ["window or door", "windows or doors"],
  room: ["room name", "room names"],
  mixed: ["part of the plan", "parts of the plan"],
};

/** "1 other wall", "2 other windows or doors", "3 other parts of the plan". */
export function otherElements(kinds: readonly PlanEditTargetKind[]): string {
  const n = kinds.length;
  const first = kinds[0];
  const [one, many] = NOUNS[first !== undefined && kinds.every((k) => k === first) ? first : "mixed"];
  return `${n} other ${n === 1 ? one : many}`;
}
